// Launch Trajectory Calculator for RocketTrail
// Calculates and visualizes rocket launch trajectories using physics-based modeling

export class TrajectoryCalculator {
  constructor() {
    // Earth's physical constants
    this.EARTH_RADIUS = 6371000; // meters
    this.EARTH_MU = 3.986004418e14; // m³/s²
    this.GRAVITY = 9.81; // m/s² at sea level

    // Atmospheric layers (approximate)
    this.ATMOSPHERIC_LAYERS = {
      TROPOSPHERE: { altitude: 12000, density: 1.225 },
      STRATOSPHERE: { altitude: 50000, density: 0.3639 },
      MESOSPHERE: { altitude: 85000, density: 0.0395 },
      THERMOSPHERE: { altitude: 600000, density: 0.0001 }
    };
  }

  /**
   * Calculate launch trajectory from launch pad to target orbit
   * @param {Object} launchData - Launch mission data
   * @returns {Object} Trajectory data with positions, velocities, and phases
   */
  calculateLaunchTrajectory(launchData) {
    const {
      launchPad,
      rocket,
      mission,
      targetOrbit
    } = launchData;

    // Extract launch parameters
    const launchLat = launchPad.latitude;
    const launchLon = launchPad.longitude;
    const targetInclination = targetOrbit?.inclination || 28.5; // Default to ISS inclination
    const targetAltitude = targetOrbit?.altitude || 408000; // Default to ISS altitude (408km)

    // Calculate launch azimuth for target inclination
    const launchAzimuth = this.calculateLaunchAzimuth(launchLat, targetInclination);

    // Generate SINGLE SMOOTH trajectory (no phases)
    const trajectory = {
      launchPad: { latitude: launchLat, longitude: launchLon, altitude: 0 },
      azimuth: launchAzimuth,
      phases: [], // Empty - we're doing single trajectory
      positions: [],
      metadata: {
        rocketName: rocket?.name || 'Unknown Rocket',
        missionName: mission?.name || 'Unknown Mission',
        targetOrbit: targetOrbit
      }
    };

    // Calculate smooth trajectory from launch to orbit
    // For the game, we'll use a simplified model where the trajectory is affected by player choices
    // but we'll still show a realistic-looking flight path
    const smoothTrajectory = this.calculateSmoothTrajectory(launchLat, launchLon, targetAltitude, launchAzimuth);
    trajectory.positions = smoothTrajectory.positions;

    // Add a single phase for compatibility
    trajectory.phases.push({
      name: 'Launch to Orbit',
      duration: smoothTrajectory.duration,
      maxAltitude: targetAltitude,
      color: '#ff8844',
      positions: trajectory.positions
    });

    return trajectory;
  }

  /**
   * Calculate optimal launch azimuth for target orbital inclination
   * Based on actual orbital mechanics and real launch trajectories
   * @param {number} launchLat - Launch latitude in degrees
   * @param {number} targetInclination - Target orbit inclination in degrees
   * @returns {number} Launch azimuth in degrees
   */
  calculateLaunchAzimuth(launchLat, targetInclination) {
    console.log('🧭 Calculating azimuth for inclination:', targetInclination, 'at latitude:', launchLat);

    // For equatorial orbits (GEO satellites), launch due east
    // This maximizes the equatorial component and matches Earth's rotation
    if (Math.abs(targetInclination) < 1) { // Changed from < 5 to < 1 for precision
      console.log('📡 GEO mission detected - returning 90° (due east)');
      return 90; // Due east - standard for GEO launches from any site
    }

    // For polar orbits (90° inclination), calculate optimal azimuth from launch latitude
    // Higher latitudes need more northward component for polar coverage
    if (Math.abs(targetInclination - 90) < 1) { // Changed from < 5 to < 1 for precision
      console.log('🛰️ Polar mission detected - returning 0° (due north)');
      return 0; // Due north for polar orbits
    }

    // For inclined orbits (LEO, MEO), use the standard orbital mechanics formula
    // The azimuth is calculated to achieve the desired inclination from the specific launch latitude
    console.log('🛰️ LEO mission detected - calculating orbital mechanics azimuth');
    const launchLatRad = (launchLat * Math.PI) / 180;
    const inclinationRad = (targetInclination * Math.PI) / 180;

    // For prograde orbits (normal direction of Earth's rotation)
    // azimuth = arcsin(cos(i) / cos(lat)) where i is inclination, lat is launch latitude
    const sinAzimuth = Math.cos(inclinationRad) / Math.cos(launchLatRad);

    if (Math.abs(sinAzimuth) > 1) {
      // Launch not possible from this latitude for this inclination
      console.log('⚠️ Launch not possible from this latitude - defaulting to 90°');
      return 90; // Default to due east
    }

    const azimuthRad = Math.asin(sinAzimuth);
    const azimuthDeg = (azimuthRad * 180) / Math.PI;

    // Add some variation based on launch site latitude for more realistic values
    // Northern hemisphere sites tend to launch slightly more eastward
    const latitudeAdjustment = launchLat > 0 ? launchLat * 0.02 : 0;
    const finalAzimuth = Math.max(80, Math.min(100, azimuthDeg + latitudeAdjustment));

    console.log('🧭 Calculated azimuth:', finalAzimuth.toFixed(1) + '°');
    return finalAzimuth;
  }

  /**
   * Calculate smooth trajectory from launch to orbit (single curve)
   * @param {number} launchLat - Launch latitude
   * @param {number} launchLon - Launch longitude
   * @param {number} targetAltitude - Target orbital altitude in meters
   * @param {number} azimuth - Launch azimuth in degrees
   * @returns {Object} Smooth trajectory data
   */
  calculateSmoothTrajectory(launchLat, launchLon, targetAltitude, azimuth) {
    const positions = [];
    const totalDuration = 360; // 6 minutes total flight time
    const timeStep = 3; // 3 seconds between points

    // Ensure all inputs are valid numbers
    const lat = Number(launchLat) || 28.5619; // Default to Cape Canaveral
    const lon = Number(launchLon) || -80.5773;
    const alt = Number(targetAltitude) || 408000; // Default to 408km
    const az = Number(azimuth) || 90; // Default to due east
    const azimuthRad = (az * Math.PI) / 180;

    console.log('🚀 Calculating smooth trajectory:');
    console.log(`   Launch: ${lat.toFixed(2)}°, ${lon.toFixed(2)}°`);
    console.log(`   Target altitude: ${(alt/1000).toFixed(0)}km`);
    console.log(`   Azimuth: ${az.toFixed(1)}°`);
    console.log(`   Duration: ${totalDuration}s, Step: ${timeStep}s`);

    for (let t = 0; t <= totalDuration; t += timeStep) {
      const progress = t / totalDuration; // 0 to 1

      // Smooth altitude curve using sine function for realistic launch profile
      // Starts slow, accelerates, then levels off
      const altitudeProgress = Math.sin(progress * Math.PI / 2); // Sine curve from 0 to 1
      const altitude = altitudeProgress * targetAltitude;

      // Velocity increases with altitude (simplified model)
      const baseVelocity = 500; // m/s minimum velocity
      const maxVelocity = 7800; // m/s orbital velocity
      const velocity = baseVelocity + (maxVelocity - baseVelocity) * altitudeProgress;

      // Pitch angle: starts vertical (90°), becomes horizontal (0°) using cosine
      const pitchAngle = 90 * Math.cos(progress * Math.PI / 2); // 90° to 0°

      // Calculate horizontal distance based on velocity and pitch
      const pitchRad = (pitchAngle * Math.PI) / 180;
      const horizontalVelocity = velocity * Math.cos(pitchRad);
      const verticalVelocity = velocity * Math.sin(pitchRad);

      // Distance traveled in this time step
      const horizontalDistance = horizontalVelocity * timeStep;
      const verticalDistance = verticalVelocity * timeStep;

      // Convert to lat/lon coordinates
      const cosLat = Math.cos(lat * Math.PI / 180);

      let deltaLat, deltaLon;
      if (Math.abs(cosLat) < 0.001) {
        // Near poles - simplified calculation
        deltaLat = (horizontalDistance * Math.cos(azimuthRad)) / 111320;
        deltaLon = Math.sign(azimuthRad) * (horizontalDistance / 111320);
      } else {
        // Normal calculation
        deltaLat = (horizontalDistance * Math.cos(azimuthRad)) / 111320;
        deltaLon = (horizontalDistance * Math.sin(azimuthRad)) / (111320 * cosLat);
      }

      // Calculate new position
      const newLat = Math.max(-90, Math.min(90, lat + deltaLat));
      const newLon = (lon + deltaLon + 180) % 360 - 180;

      positions.push({
        time: Number(t),
        latitude: Number(newLat),
        longitude: Number(newLon),
        altitude: Number(altitude),
        velocity: Number(velocity),
        pitch: Number(pitchAngle),
        phase: 'smooth_trajectory',
        atmosphericLayer: this.getAtmosphericLayer(altitude)
      });
    }

    console.log(`✅ Generated ${positions.length} smooth trajectory points`);
    console.log(`   Final position: ${positions[positions.length - 1]?.latitude.toFixed(2)}°, ${positions[positions.length - 1]?.longitude.toFixed(2)}°`);
    console.log(`   Final altitude: ${(positions[positions.length - 1]?.altitude/1000).toFixed(0)}km`);

    return {
      positions: positions,
      duration: totalDuration,
      targetAltitude: targetAltitude
    };
  }

  /**
   * Calculate vertical climb phase
   * @param {number} lat - Starting latitude
   * @param {number} lon - Starting longitude
   * @param {number} azimuth - Launch azimuth
   * @returns {Object} Phase data with positions
   */
  calculateVerticalClimb(lat, lon, azimuth) {
    const positions = [];
    const phaseDuration = 30; // seconds - shorter vertical phase
    const timeStep = 1; // second
    const maxAltitude = 3000; // 3km - much lower to reduce dominance
    const verticalVelocity = maxAltitude / phaseDuration; // m/s

    // Validate inputs
    const startLat = Number(lat);
    const startLon = Number(lon);
    const azimuthRad = (Number(azimuth) * Math.PI) / 180;

    if (isNaN(startLat) || isNaN(startLon) || isNaN(azimuthRad)) {
      console.warn('Invalid inputs for vertical climb calculation:', { startLat, startLon, azimuthRad });
      return {
        name: 'Vertical Climb',
        duration: phaseDuration,
        maxAltitude: maxAltitude,
        color: '#ff4444',
        positions: []
      };
    }

    for (let t = 0; t <= phaseDuration; t += timeStep) {
      const progress = t / phaseDuration;
      const altitude = (verticalVelocity * t);

      // Minimal horizontal movement during initial vertical climb
      const horizontalVelocity = verticalVelocity * 0.1; // 10% of vertical velocity
      const horizontalDistance = horizontalVelocity * t;

      // Convert horizontal distance to lat/lon with bounds checking
      const cosLat = Math.cos(startLat * Math.PI / 180);

      // Prevent division by zero at poles
      let deltaLat, deltaLon;
      if (Math.abs(cosLat) < 0.001) {
        // At poles, use simplified calculation
        deltaLat = (horizontalDistance * Math.cos(azimuthRad)) / 111320;
        deltaLon = Math.sign(azimuthRad) * (horizontalDistance / 111320); // Simplified at poles
      } else {
        // Normal calculation away from poles
        deltaLat = (horizontalDistance * Math.cos(azimuthRad)) / 111320;
        deltaLon = (horizontalDistance * Math.sin(azimuthRad)) / (111320 * cosLat);
      }

      const newLat = Math.max(-90, Math.min(90, startLat + deltaLat));
      const newLon = (startLon + deltaLon + 180) % 360 - 180; // Normalize longitude

      positions.push({
        time: Number(t),
        latitude: Number(newLat),
        longitude: Number(newLon),
        altitude: Number(altitude),
        velocity: Number(Math.sqrt(verticalVelocity ** 2 + horizontalVelocity ** 2)),
        pitch: Number(90 - (progress * 10)), // Start pitching slightly toward horizontal
        phase: 'vertical_climb',
        atmosphericLayer: this.getAtmosphericLayer(altitude)
      });
    }

    return {
      name: 'Vertical Climb',
      duration: phaseDuration,
      maxAltitude: maxAltitude,
      color: '#ff4444', // Red
      positions: positions
    };
  }

  /**
   * Calculate gravity turn phase
   * @param {Object} startPosition - Starting position
   * @param {number} azimuth - Launch azimuth
   * @returns {Object} Phase data with positions
   */
  calculateGravityTurn(startPosition, azimuth) {
    const positions = [];
    const phaseDuration = 90; // seconds - gravity turn duration
    const timeStep = 1.5; // seconds - more frequent points
    const startAltitude = startPosition.altitude;
    const endAltitude = 35000; // 35km - target altitude for gravity turn end
    const azimuthRad = (azimuth * Math.PI) / 180;

    // Start with the pitch from the end of vertical climb (degrees from vertical)
    const initialPitch = startPosition.pitch || 80; // ~80° from vertical = ~10° from horizontal

    console.log('Gravity Turn: Starting with pitch:', initialPitch, 'at altitude:', startAltitude);

    for (let t = 0; t <= phaseDuration; t += timeStep) {
      const progress = t / phaseDuration;
      const altitude = startAltitude + (endAltitude - startAltitude) * progress;

      // CORRECTED: Gravity turn pitch should decrease from initial pitch to horizontal
      // Pitch angle: 90° = vertical, 0° = horizontal
      const pitchAngle = initialPitch * (1 - progress); // Linear decrease to horizontal
      const pitchRad = (pitchAngle * Math.PI) / 180;

      // Velocity increases significantly during gravity turn (more realistic)
      const initialVelocity = startPosition.velocity || 300;
      const finalVelocity = 2500; // Target velocity at end of gravity turn
      const velocity = initialVelocity + (finalVelocity - initialVelocity) * progress;

      // Calculate velocity components based on pitch angle
      // pitchRad = 0° (horizontal) -> cos(0) = 1, sin(0) = 0
      // pitchRad = 90° (vertical) -> cos(90) = 0, sin(90) = 1
      const horizontalVelocity = velocity * Math.cos(pitchRad); // Max when horizontal
      const verticalVelocity = velocity * Math.sin(pitchRad);   // Max when vertical

      // Calculate position change
      const horizontalDistance = horizontalVelocity * timeStep;
      const verticalDistance = verticalVelocity * timeStep;

      // Validate inputs before calculations
      const startLat = Number(startPosition.latitude);
      const startLon = Number(startPosition.longitude);

      if (isNaN(startLat) || isNaN(startLon) || isNaN(azimuthRad)) {
        console.warn('Invalid inputs for gravity turn calculation:', { startLat, startLon, azimuthRad });
        continue;
      }

      // Convert horizontal distance to lat/lon with bounds checking
      const cosLat = Math.cos(startLat * Math.PI / 180);

      // Prevent division by zero at poles
      let deltaLat, deltaLon;
      if (Math.abs(cosLat) < 0.001) {
        // At poles, use simplified calculation
        deltaLat = (horizontalDistance * Math.cos(azimuthRad)) / 111320;
        deltaLon = Math.sign(azimuthRad) * (horizontalDistance / 111320);
      } else {
        // Normal calculation away from poles
        deltaLat = (horizontalDistance * Math.cos(azimuthRad)) / 111320;
        deltaLon = (horizontalDistance * Math.sin(azimuthRad)) / (111320 * cosLat);
      }

      // Add vertical component to altitude
      const newLat = Math.max(-90, Math.min(90, startLat + deltaLat));
      const newLon = (startLon + deltaLon + 180) % 360 - 180;
      const newAltitude = altitude + verticalDistance;

      positions.push({
        time: Number(startPosition.time + t),
        latitude: Number(newLat),
        longitude: Number(newLon),
        altitude: Number(newAltitude),
        velocity: Number(velocity),
        pitch: Number(pitchAngle),
        phase: 'gravity_turn',
        atmosphericLayer: this.getAtmosphericLayer(newAltitude)
      });
    }

    console.log('Gravity Turn: Generated', positions.length, 'positions, final pitch:', positions[positions.length - 1]?.pitch);

    return {
      name: 'Gravity Turn',
      duration: phaseDuration,
      maxAltitude: endAltitude,
      color: '#ff8844', // Orange
      positions: positions
    };
  }

  /**
   * Calculate main engine cutoff phase
   * @param {Object} startPosition - Starting position
   * @param {number} azimuth - Launch azimuth
   * @returns {Object} Phase data with positions
   */
  calculateMainEngineCutoff(startPosition, azimuth) {
    const positions = [];
    const phaseDuration = 45; // seconds - main engine cutoff duration
    const timeStep = 1.5; // seconds - more frequent points
    const startAltitude = startPosition.altitude;
    const endAltitude = 65000; // 65km - target altitude for MECO
    const azimuthRad = (azimuth * Math.PI) / 180;

    // Start with horizontal pitch from gravity turn (should be near 0°)
    const initialPitch = Math.min(startPosition.pitch || 0, 5); // Ensure horizontal flight

    console.log('Main Engine Cutoff: Starting with pitch:', initialPitch, 'at altitude:', startAltitude);

    for (let t = 0; t <= phaseDuration; t += timeStep) {
      const progress = t / phaseDuration;
      const altitude = startAltitude + (endAltitude - startAltitude) * progress;

      // CORRECTED: Maintain horizontal flight during MECO (no pitch changes)
      const pitchAngle = initialPitch; // Stay horizontal
      const pitchRad = (pitchAngle * Math.PI) / 180;

      // High suborbital velocity, increasing slightly due to coasting
      const initialVelocity = startPosition.velocity || 2500;
      const finalVelocity = initialVelocity + 200; // Slight increase due to reduced drag
      const velocity = initialVelocity + (finalVelocity - initialVelocity) * progress;

      // Calculate velocity components (should be mostly horizontal)
      const horizontalVelocity = velocity * Math.cos(pitchRad); // Near max velocity
      const verticalVelocity = velocity * Math.sin(pitchRad);   // Near zero

      // Calculate position change
      const horizontalDistance = horizontalVelocity * timeStep;
      const verticalDistance = verticalVelocity * timeStep;

      // Validate inputs before calculations
      const startLat = Number(startPosition.latitude);
      const startLon = Number(startPosition.longitude);

      if (isNaN(startLat) || isNaN(startLon) || isNaN(azimuthRad)) {
        console.warn('Invalid inputs for main engine cutoff calculation:', { startLat, startLon, azimuthRad });
        continue;
      }

      // Convert horizontal distance to lat/lon with bounds checking
      const cosLat = Math.cos(startLat * Math.PI / 180);

      // Prevent division by zero at poles
      let deltaLat, deltaLon;
      if (Math.abs(cosLat) < 0.001) {
        // At poles, use simplified calculation
        deltaLat = (horizontalDistance * Math.cos(azimuthRad)) / 111320;
        deltaLon = Math.sign(azimuthRad) * (horizontalDistance / 111320);
      } else {
        // Normal calculation away from poles
        deltaLat = (horizontalDistance * Math.cos(azimuthRad)) / 111320;
        deltaLon = (horizontalDistance * Math.sin(azimuthRad)) / (111320 * cosLat);
      }

      // Add vertical component to altitude
      const newLat = Math.max(-90, Math.min(90, startLat + deltaLat));
      const newLon = (startLon + deltaLon + 180) % 360 - 180;
      const newAltitude = altitude + verticalDistance;

      positions.push({
        time: Number(startPosition.time + t),
        latitude: Number(newLat),
        longitude: Number(newLon),
        altitude: Number(newAltitude),
        velocity: Number(velocity),
        pitch: Number(pitchAngle),
        phase: 'main_engine_cutoff',
        atmosphericLayer: this.getAtmosphericLayer(newAltitude)
      });
    }

    console.log('Main Engine Cutoff: Generated', positions.length, 'positions, final pitch:', positions[positions.length - 1]?.pitch);

    return {
      name: 'Main Engine Cutoff',
      duration: phaseDuration,
      maxAltitude: endAltitude,
      color: '#ffff44', // Yellow
      positions: positions
    };
  }

  /**
   * Calculate upper stage to orbit
   * @param {Object} startPosition - Starting position
   * @param {number} targetAltitude - Target orbital altitude in meters
   * @param {number} azimuth - Launch azimuth
   * @returns {Object} Phase data with positions
   */
  calculateUpperStage(startPosition, targetAltitude, azimuth) {
    const positions = [];
    const startAltitude = startPosition.altitude;
    const altitudeGain = targetAltitude - startAltitude;

    // CORRECTED: More realistic upper stage duration based on actual rocket performance
    const phaseDuration = Math.max(180, Math.min(400, altitudeGain / 1000)); // 3-6 minutes for 400km altitude gain
    const timeStep = 5; // seconds - less frequent for upper stage
    const azimuthRad = (azimuth * Math.PI) / 180;

    // Start with velocity from previous stage
    const initialVelocity = startPosition.velocity || 2500;

    console.log('Upper Stage: Starting altitude:', startAltitude, 'target:', targetAltitude, 'gain:', altitudeGain);
    console.log('Upper Stage: Duration:', phaseDuration, 'seconds, initial velocity:', initialVelocity);

    for (let t = 0; t <= phaseDuration; t += timeStep) {
      const progress = t / phaseDuration;

      // CORRECTED: More realistic altitude progression for upper stage
      // Upper stages typically have more efficient acceleration
      const acceleration = 25; // m/s² (typical upper stage acceleration)
      const avgVelocity = initialVelocity + (acceleration * t * 0.5); // Average velocity during time step
      const altitudeIncrease = avgVelocity * timeStep * 0.7; // 70% vertical efficiency

      const altitude = Math.min(targetAltitude, startAltitude + (altitudeGain * progress) + altitudeIncrease);

      // CORRECTED: More realistic velocity calculation
      const velocity = initialVelocity + (acceleration * t);

      // Calculate orbital velocity at current altitude for comparison
      const orbitalRadius = this.EARTH_RADIUS + altitude;
      const circularVelocity = Math.sqrt(this.EARTH_MU / orbitalRadius);

      // Use the higher of current velocity or circular velocity
      const finalVelocity = Math.max(velocity, circularVelocity);

      // Calculate horizontal distance based on velocity (mostly horizontal flight)
      const horizontalDistance = finalVelocity * timeStep * 0.95; // 95% horizontal efficiency

      // Validate inputs before calculations
      const startLat = Number(startPosition.latitude);
      const startLon = Number(startPosition.longitude);

      if (isNaN(startLat) || isNaN(startLon) || isNaN(azimuthRad)) {
        console.warn('Invalid inputs for upper stage calculation:', { startLat, startLon, azimuthRad });
        continue;
      }

      // Convert to lat/lon with proper azimuth and bounds checking
      const cosLat = Math.cos(startLat * Math.PI / 180);

      // Prevent division by zero at poles
      let deltaLat, deltaLon;
      if (Math.abs(cosLat) < 0.001) {
        // At poles, use simplified calculation
        deltaLat = (horizontalDistance * Math.cos(azimuthRad)) / 111320;
        deltaLon = Math.sign(azimuthRad) * (horizontalDistance / 111320);
      } else {
        // Normal calculation away from poles
        deltaLat = (horizontalDistance * Math.cos(azimuthRad)) / 111320;
        deltaLon = (horizontalDistance * Math.sin(azimuthRad)) / (111320 * cosLat);
      }

      // Add vertical component to altitude (minimal vertical movement in upper stage)
      const verticalDistance = finalVelocity * timeStep * 0.05; // 5% vertical component
      const newLat = Math.max(-90, Math.min(90, startLat + deltaLat));
      const newLon = (startLon + deltaLon + 180) % 360 - 180;
      const newAltitude = Math.min(targetAltitude, altitude + verticalDistance);

      positions.push({
        time: Number(startPosition.time + t),
        latitude: Number(newLat),
        longitude: Number(newLon),
        altitude: Number(newAltitude),
        velocity: Number(finalVelocity),
        pitch: Number(5), // Nearly horizontal flight
        phase: 'upper_stage',
        atmosphericLayer: this.getAtmosphericLayer(newAltitude)
      });
    }

    console.log('Upper Stage: Generated', positions.length, 'positions');
    console.log('Upper Stage: Final altitude:', positions[positions.length - 1]?.altitude, 'target:', targetAltitude);

    return {
      name: 'Upper Stage to Orbit',
      duration: phaseDuration,
      maxAltitude: targetAltitude,
      color: '#4444ff', // Blue
      positions: positions
    };
  }

  /**
   * Get atmospheric layer for given altitude
   * @param {number} altitude - Altitude in meters
   * @returns {string} Atmospheric layer name
   */
  getAtmosphericLayer(altitude) {
    if (altitude < this.ATMOSPHERIC_LAYERS.TROPOSPHERE.altitude) return 'Troposphere';
    if (altitude < this.ATMOSPHERIC_LAYERS.STRATOSPHERE.altitude) return 'Stratosphere';
    if (altitude < this.ATMOSPHERIC_LAYERS.MESOSPHERE.altitude) return 'Mesosphere';
    if (altitude < this.ATMOSPHERIC_LAYERS.THERMOSPHERE.altitude) return 'Thermosphere';
    return 'Exosphere';
  }

  /**
   * Get trajectory data formatted for visualization
   * @param {Object} trajectory - Trajectory data
   * @returns {Object} Formatted trajectory data
   */
  getFormattedTrajectory(trajectory) {
    return {
      ...trajectory,
      // Positions are already in lat/lon/alt format suitable for conversion
      formattedPhases: trajectory.phases.map(phase => ({
        ...phase,
        positionCount: phase.positions.length
      }))
    };
  }

  /**
   * Calculate trajectory for game with player parameters affecting outcome
   * @param {Object} launchData - Launch mission data
   * @param {number} playerAzimuth - Player's chosen azimuth (degrees)
   * @param {number} playerBoostDuration - Player's chosen boost duration (seconds)
   * @returns {Object} Trajectory data with positions, velocities, and phases
   */
  calculateGameTrajectory(launchData, playerAzimuth, playerBoostDuration) {
    const {
      launchPad,
      rocket,
      mission,
      targetOrbit
    } = launchData;

    // Extract launch parameters
    const launchLat = launchPad.latitude;
    const launchLon = launchPad.longitude;
    const targetInclination = targetOrbit?.inclination ?? 28.5;
    const targetAltitude = targetOrbit?.altitude ?? 408000;

    // Calculate optimal parameters for this specific mission
    const optimalAzimuth = this.calculateLaunchAzimuth(launchLat, targetInclination);

    // Fixed optimal boost durations based on mission type
    let optimalBoost;
    if (Math.abs(targetInclination) < 1) { // GEO missions (equatorial, inclination ~0°)
      optimalBoost = 580; // Fixed at 580s for GEO
    } else if (Math.abs(targetInclination - 90) < 1) { // Polar missions (inclination ~90°)
      optimalBoost = 450; // Fixed at 450s for Polar
    } else { // LEO missions (inclined orbits)
      optimalBoost = 550; // Fixed at 550s for LEO
    }

    // Player's azimuth affects the final trajectory
    // If player chooses wrong azimuth, they get wrong final position
    const azimuthError = Math.abs(playerAzimuth - optimalAzimuth);
    const azimuthMultiplier = Math.max(0.1, 1 - (azimuthError / 180)); // 0.1 to 1.0

    const boostError = Math.abs(playerBoostDuration - optimalBoost);
    const boostMultiplier = Math.max(0.2, 1 - (boostError / 200)); // 0.2 to 1.0, narrower tolerance for challenge

    console.log('🎮 Game trajectory calculation:');
    console.log(`   Mission: ${mission?.name || 'Unknown'}`);
    console.log(`   Target inclination: ${targetInclination}°`);
    console.log(`   Target altitude: ${(targetAltitude/1000).toFixed(0)}km`);
    console.log(`   Optimal azimuth: ${optimalAzimuth.toFixed(1)}°`);
    console.log(`   Player azimuth: ${playerAzimuth}° (error: ${azimuthError.toFixed(1)}°)`);
    console.log(`   Optimal boost: ${optimalBoost}s`);
    console.log(`   Player boost: ${playerBoostDuration}s (error: ${boostError.toFixed(0)}s)`);
    console.log(`   Azimuth multiplier: ${azimuthMultiplier.toFixed(2)}`);
    console.log(`   Boost multiplier: ${boostMultiplier.toFixed(2)}`);

    // Generate trajectory with player errors affecting outcome
    const trajectory = {
      launchPad: { latitude: launchLat, longitude: launchLon, altitude: 0 },
      azimuth: playerAzimuth, // Use player's azimuth
      phases: [],
      positions: [],
      metadata: {
        rocketName: rocket?.name || 'Unknown Rocket',
        missionName: mission?.name || 'Unknown Mission',
        targetOrbit: targetOrbit,
        playerAzimuth: playerAzimuth,
        playerBoostDuration: playerBoostDuration,
        azimuthMultiplier: azimuthMultiplier,
        boostMultiplier: boostMultiplier,
        optimalAzimuth: optimalAzimuth,
        optimalBoost: optimalBoost
      }
    };

    // Calculate trajectory affected by player choices
    const smoothTrajectory = this.calculateGameSmoothTrajectory(
      launchLat,
      launchLon,
      targetAltitude,
      playerAzimuth,
      azimuthMultiplier,
      boostMultiplier
    );
    trajectory.positions = smoothTrajectory.positions;

    // Add a single phase for compatibility
    trajectory.phases.push({
      name: 'Launch to Orbit',
      duration: smoothTrajectory.duration,
      maxAltitude: targetAltitude,
      color: '#ff8844',
      positions: trajectory.positions
    });

    return trajectory;
  }

  /**
   * Calculate game trajectory where player parameters affect final outcome
   * @param {number} launchLat - Launch latitude
   * @param {number} launchLon - Launch longitude
   * @param {number} targetAltitude - Target orbital altitude in meters
   * @param {number} playerAzimuth - Player's chosen azimuth
   * @param {number} azimuthMultiplier - How well the azimuth matches optimal (0.1-1.0)
   * @param {number} boostMultiplier - How well the boost duration matches optimal (0.1-1.0)
   * @returns {Object} Trajectory data
   */
  calculateGameSmoothTrajectory(launchLat, launchLon, targetAltitude, playerAzimuth, azimuthMultiplier, boostMultiplier) {
    const positions = [];
    const totalDuration = 360; // 6 minutes total flight time
    const timeStep = 3; // 3 seconds between points

    // Ensure all inputs are valid numbers
    const lat = Number(launchLat) || 28.5619;
    const lon = Number(launchLon) || -80.5773;
    const alt = Number(targetAltitude) || 408000;
    const az = Number(playerAzimuth) || 90;
    const azimuthRad = (az * Math.PI) / 180;

    console.log('🚀 Calculating game trajectory:');
    console.log(`   Launch: ${lat.toFixed(2)}°, ${lon.toFixed(2)}°`);
    console.log(`   Target altitude: ${(alt/1000).toFixed(0)}km`);
    console.log(`   Player azimuth: ${az.toFixed(1)}°`);
    console.log(`   Azimuth multiplier: ${azimuthMultiplier.toFixed(2)}`);
    console.log(`   Boost multiplier: ${boostMultiplier.toFixed(2)}`);

    for (let t = 0; t <= totalDuration; t += timeStep) {
      const progress = t / totalDuration; // 0 to 1

      // Altitude curve affected by both boost multiplier and azimuth efficiency
      // Better boost duration = closer to target altitude
      // Better azimuth = more efficient ascent (less energy wasted on wrong direction)
      const baseAltitudeProgress = Math.sin(progress * Math.PI / 2);
      const altitudeProgress = baseAltitudeProgress * boostMultiplier * azimuthMultiplier;
      const altitude = altitudeProgress * targetAltitude;

      // Velocity increases with altitude (simplified model)
      const baseVelocity = 500; // m/s minimum velocity
      const maxVelocity = 7800; // m/s orbital velocity
      const velocity = baseVelocity + (maxVelocity - baseVelocity) * altitudeProgress;

      // Pitch angle: starts vertical (90°), becomes horizontal (0°) using cosine
      const pitchAngle = 90 * Math.cos(progress * Math.PI / 2); // 90° to 0°

      // Calculate horizontal distance based on velocity and pitch
      // Azimuth affects horizontal movement direction and efficiency
      const pitchRad = (pitchAngle * Math.PI) / 180;
      const horizontalVelocity = velocity * Math.cos(pitchRad) * azimuthMultiplier; // Azimuth affects horizontal efficiency
      const verticalVelocity = velocity * Math.sin(pitchRad);

      // Distance traveled in this time step
      const horizontalDistance = horizontalVelocity * timeStep;
      const verticalDistance = verticalVelocity * timeStep;

      // Convert to lat/lon coordinates
      const cosLat = Math.cos(lat * Math.PI / 180);

      let deltaLat, deltaLon;
      if (Math.abs(cosLat) < 0.001) {
        // Near poles - simplified calculation
        deltaLat = (horizontalDistance * Math.cos(azimuthRad)) / 111320;
        deltaLon = Math.sign(azimuthRad) * (horizontalDistance / 111320);
      } else {
        // Normal calculation
        deltaLat = (horizontalDistance * Math.cos(azimuthRad)) / 111320;
        deltaLon = (horizontalDistance * Math.sin(azimuthRad)) / (111320 * cosLat);
      }

      // Calculate new position
      const newLat = Math.max(-90, Math.min(90, lat + deltaLat));
      const newLon = (lon + deltaLon + 180) % 360 - 180;

      positions.push({
        time: Number(t),
        latitude: Number(newLat),
        longitude: Number(newLon),
        altitude: Number(altitude),
        velocity: Number(velocity),
        pitch: Number(pitchAngle),
        phase: 'smooth_trajectory',
        atmosphericLayer: this.getAtmosphericLayer(altitude)
      });
    }

    console.log(`✅ Generated ${positions.length} game trajectory points`);
    console.log(`   Final position: ${positions[positions.length - 1]?.latitude.toFixed(2)}°, ${positions[positions.length - 1]?.longitude.toFixed(2)}°`);
    console.log(`   Final altitude: ${(positions[positions.length - 1]?.altitude/1000).toFixed(0)}km`);

    return {
      positions: positions,
      duration: totalDuration,
      targetAltitude: targetAltitude
    };
  }

  /**
   * Generate sample trajectory for testing
   * @returns {Object} Sample trajectory data
   */
  generateSampleTrajectory() {
    const sampleLaunchData = {
      launchPad: {
        latitude: 28.5619, // Cape Canaveral
        longitude: -80.5773,
        name: "Cape Canaveral Space Force Station"
      },
      rocket: {
        name: "Falcon 9",
        stages: 2
      },
      mission: {
        name: "Sample Mission"
      },
      targetOrbit: {
        altitude: 408000, // 408km
        inclination: 51.6 // ISS inclination
      }
    };

    return this.calculateLaunchTrajectory(sampleLaunchData);
  }
}

// Export singleton instance
export const trajectoryCalculator = new TrajectoryCalculator();
